import { useQueryClient } from "@tanstack/react-query";
import { Download, Loader2, Trash2, Upload } from "lucide-react";
import { ChangeEvent, FC, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { getListProjectsQueryKey } from "~/api/folders/folders";
import { DemoCase } from "~/api/model";
import {
  getDemoCaseReferenceReportDownloadUrl,
  getListDemoCasesQueryKey,
  useDeleteDemoCaseReferenceReport,
  useImportDefaultCase,
  useListDemoCases,
  useRemoveDemoCase,
} from "~/api/organizations/organizations";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/dialog";
import { Progress } from "~/components/ui/progress";
import { Skeleton } from "~/components/ui/skeleton";
import { MAX_FILE_SIZE_BYTES } from "~/features/settings/components/reports/importExportUtils";
import { getStoredUsername, ROUTES, useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { enableForEnv, Stages } from "~/lib/utils";
import { trackEvent } from "~/utils/analytics";
import { useDemoCaseReferenceReportUpload } from "../hooks/useDemoCaseReferenceReportUpload";

const ACCEPTED_FILE_TYPES = "application/pdf,.md,.markdown";
const MAX_FILE_SIZE_LABEL = "25 MB";

type Confirmation =
  | { kind: "demoCase"; demoCase: DemoCase }
  | { kind: "report"; demoCase: DemoCase; reportId: string }
  | null;

const formatFileSize = (sizeBytes: number) =>
  `${(sizeBytes / (1024 * 1024)).toFixed(2)} MB`;

const DemoCaseCard: FC<{
  demoCase: DemoCase;
  onConfirm: (confirmation: Exclude<Confirmation, null>) => void;
  onError: (message: string) => void;
  onClearError: () => void;
}> = ({ demoCase, onConfirm, onError, onClearError }) => {
  const intl = useIntl();
  const { organizationId } = useOrganizationParams();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDownloading, setIsDownloading] = useState<string | null>(null);
  const { toast } = useToast();
  const upload = useDemoCaseReferenceReportUpload(organizationId, demoCase.id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutateAsync: importDefaultCaseAsync, isPending: isCreating } =
    useImportDefaultCase();

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    onClearError();
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      onError(
        intl.formatMessage(
          {
            id: "demoCases.fileTooLarge",
            defaultMessage:
              "That file is too large. Please choose a file under {maxSize}.",
          },
          { maxSize: MAX_FILE_SIZE_LABEL }
        )
      );
      return;
    }
    try {
      await upload.uploadFile(file);
      toast({
        title: intl.formatMessage({
          id: "demoCases.uploadSuccess",
          defaultMessage: "Reference report attached — nice!",
        }),
      });
    } catch {
      onError(
        intl.formatMessage({
          id: "demoCases.uploadFailed",
          defaultMessage:
            "We couldn't attach that reference report. Check the file and try again.",
        })
      );
    }
  };

  const handleCreate = async () => {
    onClearError();
    const username = getStoredUsername();
    if (!username) {
      toast({
        title: intl.formatMessage({
          id: "demoCases.createUnavailable",
          defaultMessage: "Cannot create demo case",
        }),
        description: intl.formatMessage({
          id: "demoCases.createUnavailableDescription",
          defaultMessage:
            "No stored username found. Sign out and sign in again, then retry.",
        }),
        variant: "destructive",
      });
      return;
    }

    trackEvent({
      eventName: "app/demo_cases_panel/create_demo_case/click",
    });

    try {
      const response = await importDefaultCaseAsync({
        organizationId,
        data: {
          username,
          caseName: demoCase.caseName,
          industry: demoCase.industry,
        },
      });

      toast({
        title: intl.formatMessage({
          id: "demoCases.createSuccess",
          defaultMessage: "Demo case created",
        }),
        description: intl.formatMessage(
          {
            id: "demoCases.createSuccessDescription",
            defaultMessage: "Resolved: {caseName} / {industry}",
          },
          {
            caseName: response.resolvedCaseName,
            industry: response.resolvedIndustry,
          }
        ),
      });

      queryClient.invalidateQueries({
        queryKey: getListProjectsQueryKey(organizationId),
      });

      navigate(
        ROUTES.CASE({
          organizationId,
          caseId: response.newFolderId,
        })
      );
    } catch {
      toast({
        title: intl.formatMessage({
          id: "demoCases.createFailed",
          defaultMessage: "Create failed",
        }),
        description: intl.formatMessage({
          id: "demoCases.createFailedDescription",
          defaultMessage: "Could not create the demo case. Please try again.",
        }),
        variant: "destructive",
      });
    }
  };

  const handleDownload = async (reportId: string) => {
    onClearError();
    setIsDownloading(reportId);
    const popup = window.open("", "_blank");
    if (!popup) {
      onError(
        intl.formatMessage({
          id: "demoCases.downloadBlocked",
          defaultMessage:
            "We couldn't open the report download. Please allow pop-ups and try again.",
        })
      );
      setIsDownloading(null);
      return;
    }
    popup.opener = null;
    try {
      const { url } = await getDemoCaseReferenceReportDownloadUrl(
        organizationId,
        demoCase.id,
        reportId
      );
      popup.location.href = url;
    } catch {
      popup.close();
      onError(
        intl.formatMessage({
          id: "demoCases.downloadFailed",
          defaultMessage:
            "We couldn't prepare that report for download. Please try again.",
        })
      );
    } finally {
      setIsDownloading(null);
    }
  };

  return (
    <div className="rounded-lg border p-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">{demoCase.caseName}</h3>
          {demoCase.folderName && (
            <p className="text-sm">{demoCase.folderName}</p>
          )}
          <p className="text-sm text-muted-foreground">{demoCase.industry}</p>
          {demoCase.description && (
            <p className="mt-1 text-sm text-muted-foreground">
              {demoCase.description}
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            {demoCase.isFallback && (
              <Badge variant="secondary">
                <FormattedMessage
                  id="demoCases.fallback"
                  defaultMessage="Fallback"
                />
              </Badge>
            )}
            {demoCase.deprecated && (
              <Badge variant="outline">
                <FormattedMessage
                  id="demoCases.deprecated"
                  defaultMessage="Deprecated"
                />
              </Badge>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={upload.isUploading || isCreating}
          onClick={() => inputRef.current?.click()}
        >
          {upload.isUploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {upload.uploadProgress}%
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              <FormattedMessage
                id="demoCases.attachReport"
                defaultMessage="Attach Report Sample"
              />
            </>
          )}
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={upload.isUploading || isCreating}
          onClick={() => void handleCreate()}
        >
          {isCreating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              <FormattedMessage
                id="demoCases.creating"
                defaultMessage="Creating…"
              />
            </>
          ) : (
            <FormattedMessage
              id="demoCases.create"
              defaultMessage="Create Demo Case"
            />
          )}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_FILE_TYPES}
          className="hidden"
          disabled={upload.isUploading}
          onChange={handleFileChange}
        />
      </div>
      {upload.isUploading && (
        <div>
          <Progress value={upload.uploadProgress} />
          <p className="mt-1 text-right text-xs text-muted-foreground">
            {upload.uploadProgress}%
          </p>
        </div>
      )}
      <div className="space-y-2">
        <h4 className="text-sm font-medium">
          <FormattedMessage
            id="demoCases.referenceReports"
            defaultMessage="Reference Reports"
          />
        </h4>
        {demoCase.referenceReports.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            <FormattedMessage
              id="demoCases.noReports"
              defaultMessage="No reports attached yet — give this case a helpful example."
            />
          </p>
        ) : (
          demoCase.referenceReports.map((report) => (
            <div
              key={report.id}
              className="flex items-center justify-between gap-2 rounded border px-3 py-2 text-sm"
            >
              <span className="min-w-0 truncate">
                {report.filename} · {formatFileSize(report.sizeBytes)}
              </span>
              <span className="flex shrink-0 gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={intl.formatMessage({
                    id: "demoCases.download",
                    defaultMessage: "Download report",
                  })}
                  disabled={isDownloading === report.id}
                  onClick={() => void handleDownload(report.id)}
                >
                  {isDownloading === report.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={intl.formatMessage({
                    id: "demoCases.deleteReport",
                    defaultMessage: "Delete report",
                  })}
                  disabled={upload.isUploading}
                  onClick={() =>
                    onConfirm({
                      kind: "report",
                      demoCase,
                      reportId: report.id,
                    })
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </span>
            </div>
          ))
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="text-destructive"
        disabled={upload.isUploading}
        onClick={() => onConfirm({ kind: "demoCase", demoCase })}
      >
        <Trash2 className="mr-2 h-4 w-4" />
        <FormattedMessage
          id="demoCases.remove"
          defaultMessage="Remove From Demo Cases"
        />
      </Button>
    </div>
  );
};

export const DemoCasesDialog: FC = () => {
  const isDevStage = enableForEnv([Stages.DEV, Stages.LOCAL]);
  const { organizationId } = useOrganizationParams();
  const intl = useIntl();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState<Confirmation>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { data, isLoading, isError, refetch } = useListDemoCases(
    organizationId,
    {
      query: { enabled: open && isDevStage },
    }
  );
  const { mutateAsync: removeDemoCase, isPending: isRemoving } =
    useRemoveDemoCase();
  const { mutateAsync: deleteReport, isPending: isDeletingReport } =
    useDeleteDemoCaseReferenceReport();

  if (!isDevStage) return null;

  const handleConfirm = async () => {
    if (!confirmation) return;
    setErrorMessage(null);
    try {
      if (confirmation.kind === "demoCase") {
        await removeDemoCase({
          organizationId,
          demoCaseId: confirmation.demoCase.id,
        });
        toast({
          title: intl.formatMessage({
            id: "demoCases.removeSuccess",
            defaultMessage: "Demo case removed from the fixture shelf.",
          }),
        });
      } else {
        await deleteReport({
          organizationId,
          demoCaseId: confirmation.demoCase.id,
          reportId: confirmation.reportId,
        });
        toast({
          title: intl.formatMessage({
            id: "demoCases.deleteSuccess",
            defaultMessage: "Reference report deleted.",
          }),
        });
      }
      await queryClient.invalidateQueries({
        queryKey: getListDemoCasesQueryKey(organizationId),
      });
      setConfirmation(null);
      setErrorMessage(null);
    } catch {
      setConfirmation(null);
      setErrorMessage(
        confirmation.kind === "demoCase"
          ? intl.formatMessage({
              id: "demoCases.removeFailed",
              defaultMessage:
                "We couldn't remove that demo case. The underlying case is still safe — please try again.",
            })
          : intl.formatMessage({
              id: "demoCases.deleteFailed",
              defaultMessage:
                "We couldn't delete that reference report. Please try again.",
            })
      );
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setErrorMessage(null);
    } else {
      void refetch?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <FormattedMessage
            id="demoCases.manage"
            defaultMessage="DEV only: Manage Demo Cases"
          />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              id="demoCases.title"
              defaultMessage="Demo Case Fixtures"
            />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage
              id="demoCases.description"
              defaultMessage="Keep a tidy shelf of cases and reference reports for evaluation runs."
            />
          </DialogDescription>
        </DialogHeader>
        {errorMessage && (
          <p
            role="alert"
            className="rounded bg-destructive/10 p-3 text-sm text-destructive"
          >
            {errorMessage}
          </p>
        )}
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : isError ? (
          <p role="alert" className="text-sm text-destructive">
            <FormattedMessage
              id="demoCases.loadFailed"
              defaultMessage="We couldn't load the demo cases. Please close and try again."
            />
          </p>
        ) : data?.length ? (
          <div className="space-y-3">
            {data.map((demoCase) => (
              <DemoCaseCard
                key={demoCase.id}
                demoCase={demoCase}
                onConfirm={(nextConfirmation) => {
                  setErrorMessage(null);
                  setConfirmation(nextConfirmation);
                }}
                onError={setErrorMessage}
                onClearError={() => setErrorMessage(null)}
              />
            ))}
          </div>
        ) : (
          <p className="rounded border border-dashed p-8 text-center text-sm text-muted-foreground">
            <FormattedMessage
              id="demoCases.empty"
              defaultMessage="Nothing here yet — add a case from the case actions menu to get started."
            />
          </p>
        )}
      </DialogContent>
      <AlertDialog
        open={confirmation !== null}
        onOpenChange={(nextOpen) => !nextOpen && setConfirmation(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmation?.kind === "demoCase" ? (
                <FormattedMessage
                  id="demoCases.confirmRemoveTitle"
                  defaultMessage="Remove This Demo Case?"
                />
              ) : (
                <FormattedMessage
                  id="demoCases.confirmDeleteTitle"
                  defaultMessage="Delete This Reference Report?"
                />
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmation?.kind === "demoCase" ? (
                <FormattedMessage
                  id="demoCases.confirmRemoveDescription"
                  defaultMessage="This removes the demo fixture only. The underlying case and its files will not be deleted."
                />
              ) : (
                <FormattedMessage
                  id="demoCases.confirmDeleteDescription"
                  defaultMessage="This removes the report from the fixture bundle. You can attach it again later."
                />
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving || isDeletingReport}>
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isRemoving || isDeletingReport}
              onClick={(event) => {
                event.preventDefault();
                void handleConfirm();
              }}
            >
              {isRemoving || isDeletingReport ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FormattedMessage
                  id="common.confirm"
                  defaultMessage="Confirm"
                />
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
